import React, {useEffect, useState} from 'react';
import {ProColumns, ProTable} from '@ant-design/pro-components';
import {
    Avatar,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Tabs,
    Tag,
    Typography
} from 'antd';
import {EditOutlined, EyeOutlined, InfoCircleOutlined, UserOutlined} from '@ant-design/icons';
import {useAtom, useSetAtom} from 'jotai';
import dayjs from 'dayjs';
import {useContent, usePosts, useReels, useStories, useTopics, useTrackedCreators} from '../api';
import {InstagramContent, InstagramPost, InstagramReel, InstagramStory} from '../types';
import InstagramThumbnail from './InstagramThumbnail';
import {
    contentFiltersAtom,
    contentPaginationAtom,
    contentQueryAtom,
    enrichModalVisibleAtom,
    selectedContentIdAtom,
} from '../atoms';

const {RangePicker} = DatePicker;
const {Text} = Typography;
const {TabPane} = Tabs;

const ContentTab: React.FC = () => {
    const [queryParams, setQueryParams] = useAtom(contentQueryAtom);
    const [filters, setFilters] = useAtom(contentFiltersAtom);
    const [pagination, setPagination] = useAtom(contentPaginationAtom);
    const setEnrichModalVisible = useSetAtom(enrichModalVisibleAtom);
    const setSelectedContentId = useSetAtom(selectedContentIdAtom);

    // State for content type tabs
    const [activeContentTab, setActiveContentTab] = useState<string>('all');

    // State for detail modal
    const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
    const [selectedContent, setSelectedContent] = useState<InstagramPost | InstagramStory | InstagramReel | null>(null);

    // Fetch data based on active tab - use unified endpoint for all content, specific endpoints for individual types
    const {data: contentData, isLoading: isAllContentLoading} = useContent({
        ...queryParams,
        ...filters,
        ...pagination,
        mediaType: activeContentTab === 'all' ? undefined : activeContentTab,
    });

    const {data: postsData, isLoading: isPostsLoading} = usePosts({
        ...queryParams,
        ...filters,
        ...pagination,
        mediaType: 'post',
    });

    const {data: storiesData, isLoading: isStoriesLoading} = useStories({
        ...queryParams,
        ...filters,
        ...pagination,
        mediaType: 'story',
    });

    const {data: reelsData, isLoading: isReelsLoading} = useReels({
        ...queryParams,
        ...filters,
        ...pagination,
        mediaType: 'reel',
    });

    const {data: topicsData} = useTopics();
    const {data: creatorsData} = useTrackedCreators();

    const topics = topicsData?.data || [];
    const creators = creatorsData?.data || [];

    // Get content based on active tab
    const content = contentData?.data || [];
    const posts = postsData?.data || [];
    const stories = storiesData?.data || [];
    const reels = reelsData?.data || [];

    // useEffect(() => {
    //     console.log('Posts data:', postsData);
    // }, [postsData]);

    const handleEnrich = (contentId: string) => {
        setSelectedContentId(contentId);
        setEnrichModalVisible(true);
    };

    const handleShowDetails = (content: InstagramPost | InstagramStory | InstagramReel) => {
        setSelectedContent(content);
        setDetailModalVisible(true);
    };

    const handleFilterChange = (key: string, value: any) => {
        setFilters((prev) => ({...prev, [key]: value}));
        // Reset to first page when filters change
        setPagination((prev) => ({...prev, page: 1}));
    };

    const handleDateRangeChange = (dates: any) => {
        setFilters((prev) => ({
            ...prev,
            start_date: dates?.[0]?.format('YYYY-MM-DD'),
            end_date: dates?.[1]?.format('YYYY-MM-DD'),
        }));
        // Reset to first page when filters change
        setPagination((prev) => ({...prev, page: 1}));
    };

    const handleTabChange = (key: string) => {
        setActiveContentTab(key);
        // Reset pagination when changing tabs
        setPagination({page: 1, page_size: pagination.page_size});
    };

    // Common columns for all content types
    const commonColumns = [
        {
            title: 'Thumbnail',
            key: 'thumbnail',
            width: 100,
            render: (_, record: any) => (
                <InstagramThumbnail
                    url={record.media_url}
                    thumbnailS3Key={record.thumbnail_s3_key}       // S3 key
                    thumbnailPublicURL={record.thumbnail_public_url} // S3 public URL (preferred)
                    mediaType={record.content_type}
                    alt={`Content by @${record.creator_username}`}
                />
            ),
            search: false,
        },
        {
            title: 'Creator',
            dataIndex: 'creator_username',
            key: 'creator',
            width: 150,
            render: (username: string, record: any) => {
                // Prioritize S3 public URL, fallback to blob data, then external service
                let avatarSrc = `https://unavatar.io/instagram/${username}`; // Default fallback
                
                if (record.creator_profile_pic_url) {
                    avatarSrc = record.creator_profile_pic_url;
                } else if (record.creator_avatar_image) {
                    avatarSrc = `data:image/jpeg;base64,${record.creator_avatar_image}`;
                }
                
                return (
                    <Space>
                        <Avatar
                            size={32}
                            src={avatarSrc}
                            icon={<UserOutlined />}
                        />
                        <Text strong>@{username}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Caption',
            dataIndex: 'caption',
            key: 'caption',
            ellipsis: true,
            width: 200,
            render: (caption: string, record: any) => (
                <Text ellipsis={{tooltip: caption || record.caption}} style={{maxWidth: 180}}>
                    {caption || record.caption}
                </Text>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'content_type',
            key: 'content_type',
            ellipsis: true,
            width: 200,
            render: (content_type: string) => (
                <Text> {content_type}</Text>
            ),
        },
        {
            title: 'Topic',
            dataIndex: 'topic',
            key: 'topic',
            width: 120,
            render: (topic: any) => {
                if (!topic) return <Text type="secondary">No topic</Text>;
                return (
                    <Tag color={topic.color} style={{color: '#fff'}}>
                        {topic.name}
                    </Tag>
                );
            },
        },
        {
            title: 'Enriched',
            dataIndex: 'is_enriched',
            key: 'enriched',
            width: 100,
            render: (enriched: boolean) => (
                <Tag color={enriched ? 'green' : 'default'}>
                    {enriched ? 'YES' : 'NO'}
                </Tag>
            ),
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 150,
            render: (timestamp: string) => dayjs(timestamp).format('MMM DD, YYYY HH:mm'),
            sorter: true,
        },
    ];

    // Post-specific columns
    const postColumns: ProColumns<InstagramPost>[] = [
        ...commonColumns as any,
        {
            title: 'Post Type',
            dataIndex: 'post_type',
            key: 'post_type',
            width: 100,
            render: (type: string) => type || 'Image',
        },
        {
            title: 'Likes',
            dataIndex: 'likes',
            key: 'likes',
            width: 80,
            render: (likes: number) => likes || '-',
        },
        {
            title: 'Comments',
            dataIndex: 'comments',
            key: 'comments',
            width: 80,
            render: (comments: number) => comments || '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record: InstagramPost) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined/>}
                        onClick={() => window.open(record.media_url, '_blank')}
                        title="View original"
                    />
                    <Button
                        type="text"
                        icon={<InfoCircleOutlined/>}
                        onClick={() => handleShowDetails(record)}
                        title="View details"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={() => handleEnrich(record.id)}
                        title="Enrich content"
                    />
                </Space>
            ),
        },
    ];

    // Story-specific columns
    const storyColumns: ProColumns<InstagramStory>[] = [
        ...commonColumns as any,
        {
            title: 'Media Type',
            dataIndex: 'media_type',
            key: 'media_type',
            width: 100,
            render: (type: string) => type || 'photo',
        },
        {
            title: 'Views',
            dataIndex: 'views',
            key: 'views',
            width: 80,
            render: (views: number) => views || '-',
        },
        {
            title: 'Expires',
            dataIndex: 'expires_at',
            key: 'expires_at',
            width: 150,
            render: (expires_at: string) => expires_at ? dayjs(expires_at).format('MMM DD, YYYY HH:mm') : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record: InstagramStory) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined/>}
                        onClick={() => window.open(record.media_url, '_blank')}
                        title="View original"
                    />
                    <Button
                        type="text"
                        icon={<InfoCircleOutlined/>}
                        onClick={() => handleShowDetails(record)}
                        title="View details"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={() => handleEnrich(record.id)}
                        title="Enrich content"
                    />
                </Space>
            ),
        },
    ];

    // Reel-specific columns
    const reelColumns: ProColumns<InstagramReel>[] = [
        ...commonColumns as any,
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            width: 100,
            render: (duration: number) => `${duration.toFixed(1)}s`,
        },
        {
            title: 'Views',
            dataIndex: 'views',
            key: 'views',
            width: 80,
            render: (views: number) => views || '-',
        },
        {
            title: 'Likes',
            dataIndex: 'likes',
            key: 'likes',
            width: 80,
            render: (likes: number) => likes || '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record: InstagramReel) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined/>}
                        onClick={() => window.open(record.media_url, '_blank')}
                        title="View original"
                    />
                    <Button
                        type="text"
                        icon={<InfoCircleOutlined/>}
                        onClick={() => handleShowDetails(record)}
                        title="View details"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={() => handleEnrich(record.id)}
                        title="Enrich content"
                    />
                </Space>
            ),
        },
    ];

    // All content columns (unified view)
    const allContentColumns: ProColumns<InstagramContent>[] = [
        ...commonColumns as any,
        // {
        //     title: 'Media Type',
        //     dataIndex: 'media_type',
        //     key: 'media_type',
        //     width: 100,
        //     render: (type: string) => {
        //         const colorMap = {
        //             post: 'blue',
        //             story: 'orange',
        //             reel: 'purple',
        //         };
        //         return <Tag color={colorMap[type as keyof typeof colorMap]}>{type.toUpperCase()}</Tag>;
        //     },
        // },
        {
            title: 'Metrics',
            key: 'metrics',
            width: 200,
            render: (_, record: InstagramContent) => (
                <Space>
                    {record.likes !== undefined && (
                        <Text>👍 {record.likes}</Text>
                    )}
                    {record.comments !== undefined && (
                        <Text>💬 {record.comments}</Text>
                    )}
                    {record.views !== undefined && (
                        <Text>👁️ {record.views}</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record: InstagramContent) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined/>}
                        onClick={() => window.open(record.media_url, '_blank')}
                        title="View original"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={() => handleEnrich(record.id)}
                        title="Enrich content"
                    />
                </Space>
            ),
        },
    ];

    // Render detail modal content based on content type
    const renderDetailModalContent = () => {
        if (!selectedContent) return null;

        // Common details
        const commonDetails = (
            <>
                <Descriptions.Item label="Creator">@{selectedContent.creator_username}</Descriptions.Item>
                <Descriptions.Item
                    label="Posted At">{dayjs(selectedContent.timestamp).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
                <Descriptions.Item label="Enriched">{selectedContent.is_enriched ? 'Yes' : 'No'}</Descriptions.Item>
                <Descriptions.Item
                    label="Matched Filter">{selectedContent.matched_filter ? 'Yes' : 'No'}</Descriptions.Item>
                {selectedContent.topic && (
                    <Descriptions.Item label="Topic">
                        <Tag color={selectedContent.topic.color} style={{color: '#fff'}}>
                            {selectedContent.topic.name}
                        </Tag>
                    </Descriptions.Item>
                )}
            </>
        );

        // Determine content type and render specific details
        if ('post_type' in selectedContent) {
            // It's a post
            const post = selectedContent as InstagramPost;
            return (
                <Descriptions bordered column={2}>
                    {commonDetails}
                    <Descriptions.Item label="Post Type">{post.post_type || 'Image'}</Descriptions.Item>
                    {/*<Descriptions.Item label="Is Carousel">{post.is_carousel ? 'Yes' : 'No'}</Descriptions.Item>*/}
                    {/*{post.is_carousel && <Descriptions.Item label="Media Count">{post.media_count}</Descriptions.Item>}*/}
                    {post.location && <Descriptions.Item label="Location">{post.location}</Descriptions.Item>}

                    {post.is_enriched && (
                        <>
                            <Descriptions.Item label="Engagement Stats" span={2}>
                                <Row gutter={16}>
                                    {post.likes !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Likes" value={post.likes}/>
                                        </Col>
                                    )}
                                    {post.comments !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Comments" value={post.comments}/>
                                        </Col>
                                    )}
                                    {post.shares !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Shares" value={post.shares}/>
                                        </Col>
                                    )}
                                    {post.saves !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Saves" value={post.saves}/>
                                        </Col>
                                    )}
                                </Row>
                            </Descriptions.Item>

                            {post.engagement_rate !== undefined && (
                                <Descriptions.Item label="Engagement Rate">
                                    {(post.engagement_rate * 100).toFixed(2)}%
                                </Descriptions.Item>
                            )}

                            {post.accounts_reached !== undefined && (
                                <Descriptions.Item label="Accounts Reached">{post.accounts_reached}</Descriptions.Item>
                            )}

                            {post.impressions !== undefined && (
                                <Descriptions.Item label="Impressions">{post.impressions}</Descriptions.Item>
                            )}

                            {post.profile_visits !== undefined && (
                                <Descriptions.Item label="Profile Visits">{post.profile_visits}</Descriptions.Item>
                            )}

                            {post.follows !== undefined && (
                                <Descriptions.Item label="Follows">{post.follows}</Descriptions.Item>
                            )}
                        </>
                    )}

                    {post.hashtags_text && (
                        <Descriptions.Item label="Hashtags" span={2}>
                            {post.hashtags_text}
                        </Descriptions.Item>
                    )}

                    {post.mentions_text && (
                        <Descriptions.Item label="Mentions" span={2}>
                            {post.mentions_text}
                        </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Caption" span={2}>
                        {post.caption}
                    </Descriptions.Item>
                </Descriptions>
            );
        } else if ('media_type' in selectedContent && 'has_sticker' in selectedContent) {
            // It's a story
            const story = selectedContent as InstagramStory;
            return (
                <Descriptions bordered column={2}>
                    {commonDetails}
                    <Descriptions.Item label="Media Type">{story.media_type}</Descriptions.Item>
                    <Descriptions.Item
                        label="Expires At">{story.expires_at ? dayjs(story.expires_at).format('YYYY-MM-DD HH:mm:ss') : 'Unknown'}</Descriptions.Item>
                    <Descriptions.Item label="Has Sticker">{story.has_sticker ? 'Yes' : 'No'}</Descriptions.Item>
                    <Descriptions.Item label="Has Text">{story.has_text ? 'Yes' : 'No'}</Descriptions.Item>

                    {story.is_enriched && (
                        <>
                            <Descriptions.Item label="View Stats" span={2}>
                                <Row gutter={16}>
                                    {story.views !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Views" value={story.views}/>
                                        </Col>
                                    )}
                                    {story.interactions !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Interactions" value={story.interactions}/>
                                        </Col>
                                    )}
                                    {story.replies !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Replies" value={story.replies}/>
                                        </Col>
                                    )}
                                    {story.shares !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Shares" value={story.shares}/>
                                        </Col>
                                    )}
                                </Row>
                            </Descriptions.Item>

                            {story.accounts_reached !== undefined && (
                                <Descriptions.Item label="Accounts Reached">{story.accounts_reached}</Descriptions.Item>
                            )}

                            {story.followers_percentage !== undefined && (
                                <Descriptions.Item label="Followers Percentage">
                                    {story.followers_percentage}%
                                </Descriptions.Item>
                            )}

                            {story.non_followers_percentage !== undefined && (
                                <Descriptions.Item label="Non-Followers Percentage">
                                    {story.non_followers_percentage}%
                                </Descriptions.Item>
                            )}

                            {story.navigation_total !== undefined && (
                                <Descriptions.Item label="Navigation Total">{story.navigation_total}</Descriptions.Item>
                            )}

                            {story.forwards !== undefined && (
                                <Descriptions.Item label="Forwards">{story.forwards}</Descriptions.Item>
                            )}

                            {story.back !== undefined && (
                                <Descriptions.Item label="Back">{story.back}</Descriptions.Item>
                            )}

                            {story.exited !== undefined && (
                                <Descriptions.Item label="Exited">{story.exited}</Descriptions.Item>
                            )}
                        </>
                    )}

                    {story.hashtags_text && (
                        <Descriptions.Item label="Hashtags" span={2}>
                            {story.hashtags_text}
                        </Descriptions.Item>
                    )}

                    {story.mentions_text && (
                        <Descriptions.Item label="Mentions" span={2}>
                            {story.mentions_text}
                        </Descriptions.Item>
                    )}

                    {story.caption && (
                        <Descriptions.Item label="Caption" span={2}>
                            {story.caption}
                        </Descriptions.Item>
                    )}
                </Descriptions>
            );
        } else if ('duration' in selectedContent && 'is_original_audio' in selectedContent) {
            // It's a reel
            const reel = selectedContent as InstagramReel;
            return (
                <Descriptions bordered column={2}>
                    {commonDetails}
                    <Descriptions.Item label="Duration">{reel.duration.toFixed(1)} seconds</Descriptions.Item>
                    <Descriptions.Item label="Original Audio">{reel.is_original_audio ? 'Yes' : 'No'}</Descriptions.Item>
                    {reel.audio_name && <Descriptions.Item label="Audio Name">{reel.audio_name}</Descriptions.Item>}

                    {reel.is_enriched && (
                        <>
                            <Descriptions.Item label="Engagement Stats" span={2}>
                                <Row gutter={16}>
                                    {reel.views !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Views" value={reel.views}/>
                                        </Col>
                                    )}
                                    {reel.likes !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Likes" value={reel.likes}/>
                                        </Col>
                                    )}
                                    {reel.comments !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Comments" value={reel.comments}/>
                                        </Col>
                                    )}
                                    {reel.shares !== undefined && (
                                        <Col span={6}>
                                            <Statistic title="Shares" value={reel.shares}/>
                                        </Col>
                                    )}
                                </Row>
                            </Descriptions.Item>

                            {reel.engagement_rate !== undefined && (
                                <Descriptions.Item label="Engagement Rate">
                                    {(reel.engagement_rate * 100).toFixed(2)}%
                                </Descriptions.Item>
                            )}

                            {reel.accounts_reached !== undefined && (
                                <Descriptions.Item label="Accounts Reached">{reel.accounts_reached}</Descriptions.Item>
                            )}

                            {reel.total_views !== undefined && (
                                <Descriptions.Item label="Total Views">{reel.total_views}</Descriptions.Item>
                            )}

                            {reel.average_watch_time_seconds !== undefined && (
                                <Descriptions.Item
                                    label="Average Watch Time">{reel.average_watch_time_seconds} seconds</Descriptions.Item>
                            )}

                            {reel.accounts_engaged !== undefined && (
                                <Descriptions.Item label="Accounts Engaged">{reel.accounts_engaged}</Descriptions.Item>
                            )}

                            {reel.follows !== undefined && (
                                <Descriptions.Item label="Follows">{reel.follows}</Descriptions.Item>
                            )}
                        </>
                    )}

                    {reel.hashtags_text && (
                        <Descriptions.Item label="Hashtags" span={2}>
                            {reel.hashtags_text}
                        </Descriptions.Item>
                    )}

                    {reel.mentions_text && (
                        <Descriptions.Item label="Mentions" span={2}>
                            {reel.mentions_text}
                        </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Caption" span={2}>
                        {reel.caption}
                    </Descriptions.Item>
                </Descriptions>
            );
        }

        return null;
    };

    return (
        <div>
            <Card style={{marginBottom: 16}}>
                <Space wrap>
                    <Select
                        placeholder="Select Creator"
                        allowClear
                        style={{width: 200}}
                        value={filters.creator_username}
                        onChange={(value) => handleFilterChange('creator_username', value)}
                        showSearch
                        optionFilterProp="children"
                    >
                        {creators.map((creator) => (
                            <Select.Option key={creator.id} value={creator.username}>
                                @{creator.username}
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        placeholder="Topic"
                        allowClear
                        style={{width: 200}}
                        value={filters.topic_id}
                        onChange={(value) => handleFilterChange('topic_id', value)}
                        showSearch
                        optionFilterProp="children"
                    >
                        {topics.map((topic) => (
                            <Select.Option key={topic.id} value={topic.id}>
                                <Tag color={topic.color} style={{margin: 0, color: '#fff'}}>
                                    {topic.name}
                                </Tag>
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        placeholder="Enrichment Status"
                        allowClear
                        style={{width: 180}}
                        value={filters.is_enriched}
                        onChange={(value) => handleFilterChange('is_enriched', value)}
                    >
                        <Select.Option value={true}>Enriched</Select.Option>
                        <Select.Option value={false}>Not Enriched</Select.Option>
                    </Select>

                    <Select
                        placeholder="Filter Match Status"
                        allowClear
                        style={{width: 180}}
                        value={filters.matched_filter}
                        onChange={(value) => handleFilterChange('matched_filter', value)}
                    >
                        <Select.Option value={true}>Matched Filter</Select.Option>
                        <Select.Option value={false}>Not Matched</Select.Option>
                    </Select>

                    <RangePicker
                        placeholder={['Start Date', 'End Date']}
                        value={
                            filters.start_date && filters.end_date
                                ? [dayjs(filters.start_date), dayjs(filters.end_date)]
                                : null
                        }
                        onChange={handleDateRangeChange}
                    />
                </Space>
            </Card>

            <Tabs activeKey={activeContentTab} onChange={handleTabChange}>
                <TabPane tab="All Content" key="all">
                    <ProTable<InstagramContent>
                        columns={allContentColumns}
                        dataSource={content}
                        rowKey="id"
                        loading={isAllContentLoading}
                        search={false}
                        toolbar={{
                            search: {
                                placeholder: 'Search captions...',
                                onSearch: (value) => handleFilterChange('search', value),
                            },
                        }}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.page_size,
                            total: contentData?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (page, pageSize) => {
                                setPagination({page, page_size: pageSize || pagination.page_size});
                            },
                        }}
                        scroll={{x: 1200}}
                    />
                </TabPane>

                <TabPane tab="Posts" key="post">
                    <ProTable<InstagramPost>
                        columns={postColumns}
                        dataSource={posts}
                        rowKey="id"
                        loading={isPostsLoading}
                        search={false}
                        toolbar={{
                            search: {
                                placeholder: 'Search captions...',
                                onSearch: (value) => handleFilterChange('search', value),
                            },
                        }}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.page_size,
                            total: postsData?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (page, pageSize) => {
                                setPagination({page, page_size: pageSize || pagination.page_size});
                            },
                        }}
                        scroll={{x: 1200}}
                    />
                </TabPane>

                <TabPane tab="Stories" key="story">
                    <ProTable<InstagramStory>
                        columns={storyColumns}
                        dataSource={stories}
                        rowKey="id"
                        loading={isStoriesLoading}
                        search={false}
                        toolbar={{
                            search: {
                                placeholder: 'Search captions...',
                                onSearch: (value) => handleFilterChange('search', value),
                            },
                        }}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.page_size,
                            total: storiesData?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (page, pageSize) => {
                                setPagination({page, page_size: pageSize || pagination.page_size});
                            },
                        }}
                        scroll={{x: 1200}}
                    />
                </TabPane>

                <TabPane tab="Reels" key="reel">
                    <ProTable<InstagramReel>
                        columns={reelColumns}
                        dataSource={reels}
                        rowKey="id"
                        loading={isReelsLoading}
                        search={false}
                        toolbar={{
                            search: {
                                placeholder: 'Search captions...',
                                onSearch: (value) => handleFilterChange('search', value),
                            },
                        }}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.page_size,
                            total: reelsData?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (page, pageSize) => {
                                setPagination({page, page_size: pageSize || pagination.page_size});
                            },
                        }}
                        scroll={{x: 1200}}
                    />
                </TabPane>
            </Tabs>

            {/* Detail Modal */}
            <Modal
                title={selectedContent ? `${selectedContent.content_type?.toUpperCase() || 'Content'} Details` : 'Content Details'}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        Close
                    </Button>,
                    selectedContent && (
                        <Button
                            key="enrich"
                            type="primary"
                            onClick={() => {
                                setDetailModalVisible(false);
                                handleEnrich(selectedContent.id);
                            }}
                        >
                            Enrich Content
                        </Button>
                    )
                ]}
                width={800}
            >
                {renderDetailModalContent()}
            </Modal>
        </div>
    );
};

export default ContentTab;
